import {
  OdrOperationDisplayStatus,
  OdrOperationStatusFilter,
} from '~/connect/types';
import { DateUtils } from '@wyz/utils';
import {
  ICriteria,
  IOdrOperation,
  IOdrOperationEditForm,
  IOdrOperationForm,
  IOdrOperationPayload,
  IOdrOperationUpdatePayload,
} from '~/connect/types';

export const ODR_OPERATION_STATUS_OPTIONS: OdrOperationStatusFilter[] = [
  'future',
  'active',
  'ended',
  'archived',
];

export function isArchived(operation: IOdrOperation): boolean {
  return operation.status === 'archived';
}

export function toDisplayStatus(
  operation: IOdrOperation,
): OdrOperationDisplayStatus {
  // An archived operation is archived for every PDV, so it outranks the per-PDV status.
  if (isArchived(operation)) {
    return 'archived';
  }

  return operation.currentCustomerStatus === 'disabled'
    ? 'disabled'
    : operation.computedStatus;
}

export function isEnabledForCurrentCustomer(operation: IOdrOperation): boolean {
  return operation.currentCustomerStatus !== 'disabled';
}

function mapFormFields(
  data: Omit<IOdrOperationForm, 'file'>,
  criteria: ICriteria[],
): Omit<IOdrOperationPayload, 'file'> {
  return {
    name: data.name,
    brands: data.brands.map((publicKey) => ({ id: publicKey })),
    startDate: DateUtils.formatTimestamp(data.startDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    endDate: DateUtils.formatTimestamp(data.endDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    criteria: data.criteria.map(({ criterion, values }) => ({
      criterion,
      values:
        criteria
          .find((criteria) => criteria.id === criterion.id)
          ?.values.filter((value) =>
            values.some((selectedValueId) => selectedValueId === value.id),
          ) ?? [],
    })),
  };
}

export function fromFormToPayload(
  data: IOdrOperationForm,
  criteria: ICriteria[],
): IOdrOperationPayload {
  return { ...mapFormFields(data, criteria), file: data.file };
}

export function fromEditFormToPayload(
  data: IOdrOperationEditForm,
  criteria: ICriteria[],
): IOdrOperationUpdatePayload {
  return { ...mapFormFields(data, criteria), file: data.file };
}

export function toEditFormDefaultValues(
  operation: IOdrOperation,
): Partial<IOdrOperationEditForm> {
  return {
    name: operation.name,
    brands: operation.brands.map((brand) => brand.id),
    startDate: new Date(operation.startDate),
    endDate: new Date(operation.endDate),
    criteria: operation.criteria.map(({ criterion, values }) => ({
      criterion,
      values: values.map((value) => value.id),
    })),
  };
}
